import React from 'react';

import { useMediaQuery } from 'src/hooks/useMediaQuery';
import { ModalWrapper } from 'src/legacy/components/Modals';
import type { ModalWrapperProps } from 'src/legacy/components/Modals/ModalWrapper';

interface AppLimitModalShellProps {
  /** Names the dialog for assistive tech; each route uses its own heading. */
  ariaLabel: string;
  /** Desktop width; on mobile the sheet fills the screen regardless. */
  widthPx: number;
  /**
   * Suppresses dismissal. Set while an irreversible step (a card charge) is in
   * flight, so closing cannot strand the user mid-payment.
   */
  isDismissDisabled?: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

/** ModalWrapper's paper is squarer and heavier than the design's card. */
const DESKTOP_PAPER_CLASS = [
  '[&_.MuiDialog-paper]:rounded-lg',
  '[&_.MuiDialog-paper]:shadow-popover-050',
].join(' ');

/**
 * The phone design is an edge-to-edge sheet, which is why it carries no corner
 * radius. `fullScreen` gets it there, but both the radius ModalWrapper's own
 * paper style sets and the width it sets from MUI's `sm` breakpoint up (600px,
 * inside the ≤640px this treats as mobile) land in the same specificity tier as
 * MUI's fullScreen class, so which wins comes down to stylesheet order. These
 * override the paper directly rather than leaving that to chance.
 */
const MOBILE_PAPER_CLASS = [
  '[&_.MuiDialog-paper]:rounded-none',
  '[&_.MuiDialog-paper]:w-full',
  '[&_.MuiDialog-paper]:max-w-full',
].join(' ');

/**
 * The chrome both app-limit paywalls sit in. The two routes differ in width and
 * in everything inside — the fee route is a compact confirm, the upgrade route a
 * plan picker — so only the dialog itself is shared.
 */
export const AppLimitModalShell: React.FC<AppLimitModalShellProps> = ({
  ariaLabel,
  widthPx,
  isDismissDisabled = false,
  onClose,
  children,
}) => {
  const isMobile = useMediaQuery('sm', 'max');

  const handleClose: ModalWrapperProps['onClose'] = () => {
    if (isDismissDisabled) return;
    onClose();
  };

  return (
    <ModalWrapper
      open
      onClose={handleClose}
      hideActions
      hideDividers
      disableGutters
      fullScreen={isMobile}
      fullWidth={isMobile}
      width={isMobile ? undefined : widthPx}
      aria-label={ariaLabel}
      className={isMobile ? MOBILE_PAPER_CLASS : DESKTOP_PAPER_CLASS}
    >
      {children}
    </ModalWrapper>
  );
};
